import { USER_ACCOUNT_TYPES } from './constants';

export enum UuidProjectType {
  EmailUnique = 0,
  EmailNonUnique = 1,
}

/**
 * Metadata for projects other than the current project. Fetched via the
 * `userContext` API.
 */
export type UserContextProjectMetadata = {
  id: number;
  isPinned?: boolean;
  name: string;
};

/**
 * Data for the current project sent in the `userContext` API response.
 */
export type UserContextProject = UserContextProjectMetadata & {
  timeZone: string;
  wysiwygEditorMode: boolean;
  token: string;
  isUuidBased: boolean;
  uuidProjectType: UuidProjectType;
  defaultSenderEmail?: string;
  /**
   * Whether custom event ingestion can be limited for Client-side API keys (for security)
   */
  customEventManageByApiKey: boolean;
};

/**
 * The type of account specified in the `userContext` API response.
 */
export type UserContextAccountType = keyof typeof USER_ACCOUNT_TYPES;

/**
 * The key used in the `featureFlags` map in the `userContext` API response.
 */
export type UserContextFeatureFlagKey = string;

/**
 * Non-RBAC role specified in the `userContext` API response.
 */
export type UserContextPermissionsLevel =
  | 'AccountManager'
  | 'SuperAdmin'
  | 'Admin'
  | 'Publisher'
  | 'Contributor'
  | 'Reader';

/**
 * The shape of the feature flags in the `userContext` API response.
 */
export type UserContextFeatureFlags = Record<UserContextFeatureFlagKey, boolean>;

/**
 * The shape of the RBAC permissions in the `userContext` API response.
 */
export type UserContextRbacPermission = number;

/**
 * The initial shape of the `userContext` Redux state on load.
 */
export type InitialUserContextReduxState = {
  isGdprAccepted: boolean;
  featureFlags: UserContextFeatureFlags;
  isLoaded: boolean;
};

/**
 * The shape of the API response from the `userContext` endpoint.
 */
export type FetchUserContextApiResponse = {
  accountType: UserContextAccountType;
  actingUser: string;
  allowEmailBasedProjectCreation: boolean;
  avatarUrl: string | null;
  canManageMessagingServiceProviders: boolean;
  companyName: string;
  email: string | null;
  featureFlags: UserContextFeatureFlags;
  firstName: string;
  isBossOrSalesBossLevel: boolean;
  isGdprAccepted: boolean;
  lastName: string;
  orgAdmin: boolean;
  orgId: number | null;
  permissions: UserContextPermissionsLevel | null;
  project: UserContextProject;
  projects: UserContextProjectMetadata[];
  rbacPermissions: UserContextRbacPermission[];
  salesforceId: string;
  userBetaFeatures: string[];
  userBetaToggles: Record<string, boolean>;
  userId: string;
  showLiveChat: boolean;
  /**
   * Zendesk JWT used to log in & identify the current user to Zendesk.
   * Used for any Plus/Premium Support customers with Zendesk Chat enabled.
   */
  zendeskJwt?: string;
};

/**
 * The shape of the `userContext` Redux state.
 */
export type UserContext = InitialUserContextReduxState & Partial<FetchUserContextApiResponse>;

/**
 * Properties used by the `userContext` module in the Redux store state.
 */
export interface UserContextRootState {
  userContext: UserContext;
}

export type ChangeUserProjectActionOptions = {
  onComplete?: () => void;
  reloadPageOnComplete?: boolean;
};
